import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  PlusCircle, 
  Megaphone, 
  Calendar, 
  Clock, 
  Trash2, 
  Edit, 
  Play,
  Pause,
  Send,
  Gift,
  Target,
  Users,
  RefreshCw,
  X,
  Upload,
  Image,
  Film,
  Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PromotionsManager = () => {
  const [promotions, setPromotions] = useState([]);
  const [brands, setBrands] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    brand_id: '',
    name: '',
    description: '',
    how_to_participate: [''],
    reward_details: '',
    terms_conditions: [''],
    prize_pool: '',
    applicable_games: '',
    start_date: '',
    end_date: '',
    recurring_type: 'none',
    recurring_days: [],
    post_times: ['10:00'],
    target_channel_ids: [],
    media_ids: []
  });
  const [saving, setSaving] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaFiles, setMediaFiles] = useState([]); // Store {id, filename, url, type}

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [promosRes, brandsRes, channelsRes] = await Promise.all([
        axios.get(`${API}/promotions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/brands`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/channels`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPromotions(promosRes.data);
      setBrands(brandsRes.data);
      setChannels(channelsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      brand_id: '',
      name: '',
      description: '',
      how_to_participate: [''],
      reward_details: '',
      terms_conditions: [''],
      prize_pool: '',
      applicable_games: '',
      start_date: '',
      end_date: '',
      recurring_type: 'none',
      recurring_days: [],
      post_times: ['10:00'],
      target_channel_ids: [],
      media_ids: []
    });
    setMediaFiles([]);
    setEditingPromo(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const openEditModal = (promo) => {
    setEditingPromo(promo);
    setFormData({
      brand_id: promo.brand_id,
      name: promo.name,
      description: promo.description,
      how_to_participate: promo.how_to_participate?.length ? promo.how_to_participate : [''],
      reward_details: promo.reward_details,
      terms_conditions: promo.terms_conditions?.length ? promo.terms_conditions : [''],
      prize_pool: promo.prize_pool || '',
      applicable_games: promo.applicable_games || '',
      start_date: promo.start_date?.split('T')[0] || '',
      end_date: promo.end_date?.split('T')[0] || '',
      recurring_type: promo.recurring_type || 'none',
      recurring_days: promo.recurring_days || [],
      post_times: promo.post_times?.length ? promo.post_times : ['10:00'],
      target_channel_ids: promo.target_channel_ids || [],
      media_ids: promo.media_ids || []
    });
    // Load existing media info
    if (promo.media_ids?.length) {
      setMediaFiles(promo.media_ids.map(id => ({
        id,
        filename: `Media ${id.substring(0, 8)}...`,
        url: `${API}/media/${id}`,
        type: 'image' // Assume image for now
      })));
    } else {
      setMediaFiles([]);
    }
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.brand_id || !formData.name || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Please set start and end dates');
      return;
    }

    if (formData.target_channel_ids.length === 0) {
      toast.error('Please select at least one target channel');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        how_to_participate: formData.how_to_participate.filter(s => s.trim()),
        terms_conditions: formData.terms_conditions.filter(s => s.trim()),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        media_ids: formData.media_ids
      };

      if (editingPromo) {
        await axios.put(`${API}/promotions/${editingPromo.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promotion updated!');
      } else {
        await axios.post(`${API}/promotions`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Promotion created!');
      }

      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save promotion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Are you sure you want to delete this promotion?')) return;

    try {
      await axios.delete(`${API}/promotions/${promoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Promotion deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete promotion');
    }
  };

  const handlePostNow = async (promoId) => {
    try {
      const result = await axios.post(`${API}/promotions/${promoId}/post-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Posted to ${result.data.results?.length || 0} channels`);
      fetchData();
    } catch (error) {
      toast.error('Failed to post promotion');
    }
  };

  const toggleStatus = async (promo) => {
    const newStatus = promo.status === 'active' ? 'paused' : 'active';
    try {
      await axios.put(`${API}/promotions/${promo.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Promotion ${newStatus}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const toggleChannel = (channelId) => {
    setFormData(prev => ({
      ...prev,
      target_channel_ids: prev.target_channel_ids.includes(channelId)
        ? prev.target_channel_ids.filter(id => id !== channelId)
        : [...prev.target_channel_ids, channelId]
    }));
  };

  const toggleDay = (day) => {
    setFormData(prev => ({
      ...prev,
      recurring_days: prev.recurring_days.includes(day)
        ? prev.recurring_days.filter(d => d !== day)
        : [...prev.recurring_days, day]
    }));
  };

  // Media upload handler
  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploadingMedia(true);
    const newMediaIds = [...formData.media_ids];
    const newMediaFiles = [...mediaFiles];

    for (const file of files) {
      try {
        const formDataFile = new FormData();
        formDataFile.append('file', file);

        const response = await axios.post(`${API}/upload`, formDataFile, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        newMediaIds.push(response.data.id);
        newMediaFiles.push({
          id: response.data.id,
          filename: file.name,
          url: response.data.url,
          type: file.type.startsWith('video') ? 'video' : 'image'
        });
        
        toast.success(`Uploaded: ${file.name}`);
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }

    setFormData(prev => ({ ...prev, media_ids: newMediaIds }));
    setMediaFiles(newMediaFiles);
    setUploadingMedia(false);
    
    // Reset file input
    e.target.value = '';
  };

  const removeMedia = (mediaId) => {
    setFormData(prev => ({
      ...prev,
      media_ids: prev.media_ids.filter(id => id !== mediaId)
    }));
    setMediaFiles(prev => prev.filter(f => f.id !== mediaId));
  };

  const filteredPromotions = promotions.filter(p => {
    if (filterBrand !== 'all' && p.brand_id !== filterBrand) return false;
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    return true;
  });

  const getChannelsForBrand = (brandId) => {
    return channels.filter(c => c.brand_id === brandId);
  };

  const getBrandName = (brandId) => {
    const brand = brands.find(b => b.id === brandId);
    return brand?.name || 'Unknown';
  };

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="promotions-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-red-400" />
            Promotions Manager
          </h1>
          <p className="text-muted-foreground mt-1">
            Create and schedule promotional campaigns across your channels
          </p>
        </div>
        <Button onClick={openCreateModal} className="bg-red-600 hover:bg-red-700">
          <PlusCircle className="w-4 h-4 mr-2" />
          New Promotion
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={filterBrand} onValueChange={setFilterBrand}>
          <SelectTrigger className="w-48 bg-gray-800 border-gray-600">
            <SelectValue placeholder="Filter by brand" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">
            <SelectItem value="all" className="text-white">All Brands</SelectItem>
            {brands.map(brand => (
              <SelectItem key={brand.id} value={brand.id} className="text-white">
                {brand.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-gray-800 border-gray-600">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-600">
            <SelectItem value="all" className="text-white">All Status</SelectItem>
            <SelectItem value="active" className="text-white">Active</SelectItem>
            <SelectItem value="paused" className="text-white">Paused</SelectItem>
            <SelectItem value="completed" className="text-white">Completed</SelectItem>
          </SelectContent>
        </Select>

        <Button onClick={fetchData} variant="outline" className="border-gray-600">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Promotions Grid */}
      {filteredPromotions.length === 0 ? (
        <Card className="bg-gray-900/50 border-gray-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Gift className="w-12 h-12 text-gray-500 mb-4" />
            <p className="text-gray-400 text-lg">No promotions yet</p>
            <p className="text-gray-500 text-sm mt-1">Create your first promotion to get started</p>
            <Button onClick={openCreateModal} className="mt-4 bg-red-600 hover:bg-red-700">
              <PlusCircle className="w-4 h-4 mr-2" />
              Create Promotion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPromotions.map(promo => (
            <Card key={promo.id} className="bg-gray-900/50 border-gray-700 hover:border-gray-600 transition-colors">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant={promo.status === 'active' ? 'default' : 'secondary'} className={promo.status === 'active' ? 'bg-green-600' : ''}>
                      {promo.status}
                    </Badge>
                    <CardTitle className="mt-2 text-lg">{promo.name}</CardTitle>
                    <CardDescription className="text-xs text-gray-400">
                      {getBrandName(promo.brand_id)}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-gray-300 line-clamp-2">{promo.description}</p>
                
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Calendar className="w-3 h-3" />
                  <span>{new Date(promo.start_date).toLocaleDateString()} - {new Date(promo.end_date).toLocaleDateString()}</span>
                </div>
                
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <Target className="w-3 h-3" />
                  <span>{promo.target_channel_ids?.length || 0} channels</span>
                  <span className="mx-1">•</span>
                  <Send className="w-3 h-3" />
                  <span>{promo.posts_made || 0} posts</span>
                  {promo.media_ids?.length > 0 && (
                    <>
                      <span className="mx-1">•</span>
                      <Image className="w-3 h-3" />
                      <span>{promo.media_ids.length} media</span>
                    </>
                  )}
                </div>

                {promo.reward_details && (
                  <div className="text-sm text-green-400 font-medium">
                    💰 {promo.reward_details}
                  </div>
                )}

                <div className="flex gap-2 pt-2">
                  <Button size="sm" variant="outline" onClick={() => handlePostNow(promo.id)} className="flex-1 border-gray-600">
                    <Send className="w-3 h-3 mr-1" />
                    Post Now
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleStatus(promo)} className="border-gray-600">
                    {promo.status === 'active' ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditModal(promo)} className="border-gray-600">
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(promo.id)} className="border-red-600 text-red-400 hover:bg-red-600/20">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-red-400" />
              {editingPromo ? 'Edit Promotion' : 'Create New Promotion'}
            </DialogTitle>
            <DialogDescription>
              Set up your promotional campaign details and scheduling
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Brand Selection */}
            <div className="space-y-2">
              <Label>Brand *</Label>
              <Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v, target_channel_ids: [] }))}>
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue placeholder="Select brand..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  {brands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id} className="text-white">
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Basic Info */}
            <div className="space-y-2">
              <Label>Promotion Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Monday Happy Hour - Instant Bonus"
                className="bg-gray-800 border-gray-600"
              />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the promotion"
                className="bg-gray-800 border-gray-600"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Reward Details *</Label>
              <Input
                value={formData.reward_details}
                onChange={(e) => setFormData(prev => ({ ...prev, reward_details: e.target.value }))}
                placeholder="e.g., Get up to ₹206 instant bonus"
                className="bg-gray-800 border-gray-600"
              />
            </div>

            {/* How to Participate */}
            <div className="space-y-2">
              <Label>How to Participate</Label>
              {formData.how_to_participate.map((step, idx) => (
                <div key={idx} className="flex gap-2">
                  <span className="text-gray-400 mt-2">{idx + 1}.</span>
                  <Input
                    value={step}
                    onChange={(e) => updateArrayItem('how_to_participate', idx, e.target.value)}
                    placeholder={`Step ${idx + 1}`}
                    className="bg-gray-800 border-gray-600 flex-1"
                  />
                  {formData.how_to_participate.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeArrayItem('how_to_participate', idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addArrayItem('how_to_participate')} className="border-gray-600">
                + Add Step
              </Button>
            </div>

            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prize Pool (optional)</Label>
                <Input
                  value={formData.prize_pool}
                  onChange={(e) => setFormData(prev => ({ ...prev, prize_pool: e.target.value }))}
                  placeholder="e.g., ₹52,00,000"
                  className="bg-gray-800 border-gray-600"
                />
              </div>
              <div className="space-y-2">
                <Label>Applicable Games (optional)</Label>
                <Input
                  value={formData.applicable_games}
                  onChange={(e) => setFormData(prev => ({ ...prev, applicable_games: e.target.value }))}
                  placeholder="e.g., Lightning Roulette"
                  className="bg-gray-800 border-gray-600"
                />
              </div>
            </div>

            {/* Media Upload Section */}
            <div className="space-y-3">
              <Label className="flex items-center gap-2">
                <Image className="w-4 h-4" />
                Promotional Media (Images/Videos)
              </Label>
              <p className="text-xs text-gray-400">
                Upload images or videos to rotate with each post. Max 10MB per file.
              </p>
              
              {/* Upload Button */}
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime"
                    onChange={handleMediaUpload}
                    className="hidden"
                    disabled={uploadingMedia}
                  />
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-600 hover:border-red-500 transition-colors ${uploadingMedia ? 'opacity-50' : ''}`}>
                    {uploadingMedia ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                    <span className="text-sm">{uploadingMedia ? 'Uploading...' : 'Upload Files'}</span>
                  </div>
                </label>
                {mediaFiles.length > 0 && (
                  <span className="text-sm text-gray-400">
                    {mediaFiles.length} file{mediaFiles.length > 1 ? 's' : ''} added
                  </span>
                )}
              </div>

              {/* Media Preview Grid */}
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {mediaFiles.map((media, idx) => (
                    <div 
                      key={media.id} 
                      className="relative group rounded-lg overflow-hidden bg-gray-800 border border-gray-700"
                    >
                      {media.type === 'video' ? (
                        <div className="aspect-square flex items-center justify-center bg-gray-900">
                          <Film className="w-8 h-8 text-gray-500" />
                        </div>
                      ) : (
                        <img 
                          src={media.url} 
                          alt={media.filename}
                          className="aspect-square object-cover"
                        />
                      )}
                      {/* Order badge */}
                      <div className="absolute top-1 left-1 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                        #{idx + 1}
                      </div>
                      {/* Remove button */}
                      <button
                        onClick={() => removeMedia(media.id)}
                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      {/* Filename tooltip */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 truncate">
                        {media.filename}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {mediaFiles.length > 1 && (
                <p className="text-xs text-gray-500">
                  Media will rotate in order (1 → 2 → 3...) with each scheduled post.
                </p>
              )}
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className="bg-gray-800 border-gray-600"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className="bg-gray-800 border-gray-600"
                />
              </div>
            </div>

            {/* Recurring */}
            <div className="space-y-2">
              <Label>Recurring Type</Label>
              <Select value={formData.recurring_type} onValueChange={(v) => setFormData(prev => ({ ...prev, recurring_type: v }))}>
                <SelectTrigger className="bg-gray-800 border-gray-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem value="none" className="text-white">No Recurring (One-time)</SelectItem>
                  <SelectItem value="daily" className="text-white">Daily</SelectItem>
                  <SelectItem value="weekly" className="text-white">Weekly</SelectItem>
                  <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.recurring_type === 'weekly' && (
              <div className="space-y-2">
                <Label>Recurring Days</Label>
                <div className="flex gap-2">
                  {dayNames.map((day, idx) => (
                    <Button
                      key={day}
                      size="sm"
                      variant={formData.recurring_days.includes(idx) ? 'default' : 'outline'}
                      onClick={() => toggleDay(idx)}
                      className={formData.recurring_days.includes(idx) ? 'bg-red-600' : 'border-gray-600'}
                    >
                      {day}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Post Times */}
            <div className="space-y-2">
              <Label>Post Times</Label>
              {formData.post_times.map((time, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    type="time"
                    value={time}
                    onChange={(e) => updateArrayItem('post_times', idx, e.target.value)}
                    className="bg-gray-800 border-gray-600 w-32"
                  />
                  {formData.post_times.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeArrayItem('post_times', idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addArrayItem('post_times')} className="border-gray-600">
                + Add Time
              </Button>
            </div>

            {/* Target Channels */}
            {formData.brand_id && (
              <div className="space-y-2">
                <Label>Target Channels *</Label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 bg-gray-800/50 rounded-lg">
                  {getChannelsForBrand(formData.brand_id).map(channel => (
                    <div
                      key={channel.id}
                      onClick={() => toggleChannel(channel.id)}
                      className={`p-2 rounded cursor-pointer border transition-colors ${
                        formData.target_channel_ids.includes(channel.id)
                          ? 'bg-red-600/20 border-red-600'
                          : 'bg-gray-800 border-gray-600 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Checkbox checked={formData.target_channel_ids.includes(channel.id)} />
                        <span className="text-sm">{channel.display_name}</span>
                      </div>
                      <span className="text-xs text-gray-400 ml-6">{channel.platform}</span>
                    </div>
                  ))}
                </div>
                {getChannelsForBrand(formData.brand_id).length === 0 && (
                  <p className="text-sm text-gray-400">No channels found for this brand</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-gray-600">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? 'Saving...' : editingPromo ? 'Update Promotion' : 'Create Promotion'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionsManager;
